import { createError, RelodeApiError } from "@relode/core";
import { TotalCountResponse } from "@relode/core/backend/responses";
import * as React from "react";
import { useHistory } from "react-router";

import { converters } from "../../../shared/backend";
import { dashboards } from "../../../shared/backend/controllers";
import { DashboardType } from "../../../shared/types";
import { creators, DashboardActionTypes } from "./dashboardActionTypes";
import { ManagerStatTypes, MemberStatTypes } from "./dashboardState";
import { convertJobStatisticsResponse } from "./types/convertJobStatisticsResponse";
import { convertCandidateInterviewResponses } from "./types/memberUpcomingInterview";

export const useDashboardActions = (
    dispatch: React.Dispatch<DashboardActionTypes>
) => {
    const history = useHistory();
    const loadJobManagerDetails = async () => {
        try {
            dispatch(creators.loadJobManagerDetails());
            const detailsResponse = await dashboards.dashboardService.getJobManagerDetails();
            const details = converters.convertJobManagerContactResponse(
                detailsResponse
            );
            dispatch(creators.loadJobManagerDetailsSuccess(details));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    err.friendlyMessages =
                        "Unable to retrieve job manager contact details.";
                    dispatch(creators.loadJobManagerDetailsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadJobManagerDetails"
            );
        }
    };

    const loadDashboardType = async (showAllJobs?: boolean) => {
        dispatch(creators.loadDashboardType(showAllJobs));
        try {
            const response = await dashboards.dashboardService.getDashboardType();
            const type =
                DashboardType.fromId(response.dashboardTypeId) ||
                DashboardType.None;
            dispatch(creators.loadDashboardTypeSuccess(type));
            if (type === DashboardType.HiringTeamManager) {
                await loadManagerStats(showAllJobs);
            } else if (type === DashboardType.HiringTeamMember) {
                await loadMemberStats();
            }
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadDashboardTypeFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadDashboardType"
            );
        }
    };

    const loadManagerStat = async (
        stat: ManagerStatTypes,
        serviceCall: (showAllJobs: boolean) => Promise<TotalCountResponse>,
        showAllJobs: boolean
    ) => {
        dispatch(creators.loadManagerDashboardCount(stat));
        try {
            const response = await serviceCall(showAllJobs);
            dispatch(
                creators.loadManagerDashboardCountSuccess(stat, response.total)
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(
                        creators.loadManagerDashboardCountFailure(stat, err)
                    );
                    return;
                }
            }
            throw createError(
                err,
                `client.dashboard.dashboardActions.loadManagerStat:${stat}`
            );
        }
    };

    const loadHiringTeamManagerStatistics = async (showAllJobs: boolean) => {
        try {
            dispatch(creators.loadManagerJobStatistics());
            const response = await dashboards.managerDashboardService.getHiringTeamManagerStatistics(
                showAllJobs
            );
            const stats = convertJobStatisticsResponse(response);
            dispatch(creators.loadManagerJobStatisticsSuccess(stats));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadManagerJobStatisticsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadHiringTeamManagerStatistics"
            );
        }
    };

    const loadManagerJobCounts = async (showAllJobs: boolean) => {
        dispatch(creators.loadManagerJobCounts());
        try {
            const response = await dashboards.managerDashboardService.getJobCountsForClientManager(
                showAllJobs
            );
            dispatch(
                creators.loadManagerJobCountsSuccess({
                    closed: response.closed,
                    open: response.open,
                    paused: response.paused,
                    pending: response.pending,
                    requested: response.requested,
                })
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadManagerJobCountsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadManagerJobCounts"
            );
        }
    };

    const loadManagerStats = async (showAllJobs: boolean) => {
        const promises = [
            loadManagerStat(
                "submitted",
                dashboards.managerDashboardService
                    .getCandidatesInSubmittedStatus,
                showAllJobs
            ),
            loadManagerStat(
                "interviewingPast",
                dashboards.managerDashboardService
                    .getCandidatesInInterviewingPastStatus,
                showAllJobs
            ),
            loadManagerStat(
                "interviewingAwaitingFeedback",
                dashboards.managerDashboardService
                    .getCandidatesInInterviewingPastAwaitingFeedbackStatus,
                showAllJobs
            ),
            loadManagerStat(
                "interviewingUpcoming",
                dashboards.managerDashboardService
                    .getCandidatesInInterviewingUpcomingdStatus,
                showAllJobs
            ),
            loadManagerStat(
                "offered",
                dashboards.managerDashboardService.getCandidatesInOfferedStatus,
                showAllJobs
            ),
            loadManagerStat(
                "hiredConfirmed",
                dashboards.managerDashboardService
                    .getCandidatesInHiredConfirmedStatus,
                showAllJobs
            ),
            loadManagerStat(
                "hiredUnconfirmed",
                dashboards.managerDashboardService
                    .getCandidatesInHiredUnconfirmedStatus,
                showAllJobs
            ),
            loadManagerJobCounts(showAllJobs),
            loadHiringTeamManagerStatistics(showAllJobs),
        ];
        await Promise.all(promises);
    };

    const loadHiringTeamMemberStat = async (
        stat: MemberStatTypes,
        serviceCall: () => Promise<TotalCountResponse>
    ) => {
        dispatch(creators.loadHiringTeamMemberDashboardCount(stat));
        try {
            const response = await serviceCall();
            dispatch(
                creators.loadMemberDashboardCountSuccess(stat, response.total)
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(
                        creators.loadMemberDashboardCountFailure(stat, err)
                    );
                    return;
                }
            }
            throw createError(
                err,
                `client.dashboard.dashboardActions.loadMemberStat:${stat}`
            );
        }
    };

    const loadMemberUpcomingInterviews = async () => {
        dispatch(creators.loadMemberUpcomingInterviews());
        try {
            const response = await dashboards.teamMemberDashboardService.getUpcomingInterviews();
            const result = convertCandidateInterviewResponses(response);
            dispatch(creators.loadMemberUpcomingInterviewsSuccess(result));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadMemberUpcomingInterviewsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadMemberUpcomingInterviews"
            );
        }
    };

    const loadMemberJobCounts = async () => {
        dispatch(creators.loadMemberJobCounts());
        try {
            const response = await dashboards.teamMemberDashboardService.getJobCountsForHiringTeamMember();
            dispatch(
                creators.loadMemberJobCountsSuccess({
                    open: response.open,
                    closed: response.closed,
                    paused: response.paused,
                })
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadMemberJobCountsFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.dashboard.dashboardActions.loadMemberJobCounts"
            );
        }
    };

    const loadMemberStats = async () => {
        await Promise.all([
            loadHiringTeamMemberStat(
                "interviewingPast",
                dashboards.teamMemberDashboardService
                    .getInterviewingPastCandidates
            ),
            loadHiringTeamMemberStat(
                "interviewingPastAwaitingFeedback",
                dashboards.teamMemberDashboardService
                    .getInterviewingPastAwaitingFeedbackCandidates
            ),
            loadHiringTeamMemberStat(
                "submitted",
                dashboards.teamMemberDashboardService.getSubmittedCandidates
            ),
            loadMemberUpcomingInterviews(),
            loadMemberJobCounts(),
        ]);
    };

    const setShowAllJobs = (showAll: boolean) => {
        history.replace(`/dashboard?showAll=${showAll}`);
    };

    return {
        loadJobManagerDetails,
        loadDashboardType,
        loadManagerStats,
        loadMemberStats,
        setShowAllJobs,
    };
};

export type DashboardActions = ReturnType<typeof useDashboardActions>;
